import React, { useEffect } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { crossfade, viewTransition, springSoft, EASE_SMOOTH, DURATION_SLOW, entranceTransition } from '../../utils/motionPresets';

function DashboardLayout({
  activeView,
  sidebarCollapsed,
  setSidebarCollapsed,
  sidebarProps = {},
  topBarProps = {},
  children,
  overlays,
}) {
  const mobileOpen = !sidebarCollapsed;

  useEffect(() => {
    if (mobileOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [mobileOpen]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const closeMobileDrawer = (event) => {
      if (!event.matches) {
        setSidebarCollapsed(true);
      }
    };
    mq.addEventListener('change', closeMobileDrawer);
    return () => mq.removeEventListener('change', closeMobileDrawer);
  }, [setSidebarCollapsed]);

  const sidebarIsNarrow = sidebarCollapsed;
  const reducedMotion = useReducedMotion();

  return (
    <div className="dashboard-shell app-shell min-h-screen">
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="sidebar-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: EASE_SMOOTH }}
            className="sidebar-overlay fixed inset-0 z-[9998] lg:hidden"
            onClick={() => setSidebarCollapsed(true)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <div className="hidden lg:block">
        <Sidebar
          activeView={activeView}
          isCollapsed={sidebarIsNarrow}
          setIsCollapsed={setSidebarCollapsed}
          {...sidebarProps}
        />
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-sidebar"
            initial={{ x: '-100%', opacity: 0.6 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0.6 }}
            transition={springSoft}
            className="fixed left-0 top-0 z-[9999] h-[100dvh] lg:hidden"
          >
            <Sidebar
              activeView={activeView}
              isCollapsed={false}
              setIsCollapsed={setSidebarCollapsed}
              onMobileNavigate={() => setSidebarCollapsed(true)}
              {...sidebarProps}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        className={`flex min-w-0 flex-col transition-[margin-left] duration-[450ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
          sidebarIsNarrow ? 'lg:ml-20' : 'lg:ml-[256px]'
        }`}
      >
        <TopBar
          {...topBarProps}
          menuOpen={mobileOpen}
          onMenuClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        />

        <motion.main
          className="dashboard-main flex-1 overflow-auto scroll-smooth"
          id="main-content"
          initial={reducedMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={entranceTransition}
        >
          <div className="container-premium section-padding content-flow">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeView}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={reducedMotion ? crossfade : viewTransition}
                transition={{ duration: DURATION_SLOW, ease: EASE_SMOOTH }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.main>
      </div>

      {overlays}
    </div>
  );
}

export default DashboardLayout;
